<template>
     <span v-if="showAllUsers || user.is_internal || user.is_admin || user.is_master">
        <emc-table-icon
            :disabled="disabled"
            :loading="loading"
            icon="mdi-history"
            :color="color"
            :tool-tip="toolTip"
            @click="open"
        />

        <emc-modal-form
            title="Logs de auditoria"
            icon="mdi-history"
            maxWidth="90%"
            v-bind:show.sync="dialog"
        >
            <template v-slot:form>
                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-card outlined>
                            <v-card-title class="py-2">
                                <span class="text-subtitle-2">Registros</span>
                                <v-spacer></v-spacer>
                                <v-btn icon :disabled="loading" @click.prevent.stop="fetchLogs">
                                    <v-icon>mdi-refresh</v-icon>
                                </v-btn>
                            </v-card-title>
                            <v-divider></v-divider>
                            <v-card-text class="py-2">
                                <v-alert v-if="errorMessage" type="error" dense text>
                                    {{ errorMessage }}
                                </v-alert>

                                <v-data-table
                                    :headers="headers"
                                    :items="logs"
                                    :loading="loading"
                                    :items-per-page="10"
                                    dense
                                    fixed-header
                                    height="360"
                                    class="elevation-0"
                                    @click:row="selectLog"
                                >
                                    <template v-slot:item.created_at="{ item }">
                                        <span>{{ item.created_at | dateTime }}</span>
                                    </template>
                                    <template v-slot:no-data>
                                        <span>Sem logs para este registro.</span>
                                    </template>
                                </v-data-table>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-card outlined>
                            <v-card-title class="py-2">
                                <span class="text-subtitle-2">Alterações</span>
                                <v-spacer></v-spacer>
                                <small v-if="selected && selected.created_at">
                                    {{ selected.created_at | dateTime }}
                                </small>
                            </v-card-title>
                            <v-divider></v-divider>
                            <v-card-text class="py-2" style="min-height: 360px;">
                                <div v-if="payloadLoading" class="text-center py-6">
                                    <v-progress-circular indeterminate color="primary" />
                                </div>

                                <div v-else-if="changes" class="audit-table-scroll">
                                    <v-simple-table dense>
                                        <thead>
                                            <tr>
                                                <th>Campo</th>
                                                <th>Antes</th>
                                                <th>Depois</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="row in changesRows"
                                                :key="row.key"
                                                :class="row.changed ? 'audit-row-changed' : ''"
                                            >
                                                <td class="text--secondary" style="vertical-align: top;">
                                                    <code>{{ row.key }}</code>
                                                </td>
                                                <td style="vertical-align: top;">
                                                    <pre class="audit-pre">{{ formatValue(row.old) }}</pre>
                                                </td>
                                                <td style="vertical-align: top;">
                                                    <pre class="audit-pre">{{ formatValue(row.new) }}</pre>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </v-simple-table>
                                </div>

                                <div v-else class="text--secondary">
                                    Clique em um registro para ver as alterações.
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </template>
        </emc-modal-form>
    </span>
</template>

<script>
import { service } from '@/helpers/http'
import { mapState } from 'vuex'

export default {
    name: 'AuditLogButton',
    props: {
        tableName: {
            type: String,
            required: true,
        },
        recordId: {
            type: [Number, String],
            required: false,
        },
        contextId: {
            type: [Number, String],
            required: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        color: {
            type: String,
            default: 'indigo',
        },
        toolTip: {
            type: String,
            default: 'Logs',
        },
        showAllUsers: {
          type: Boolean,
          default: false
        }
    },
    data: () => ({
        dialog: false,
        loading: false,
        logs: [],
        errorMessage: '',
        selected: null,
        payloadLoading: false,
        changes: null,
        headers: [
            { text: 'Data', value: 'created_at', sortable: false },
            { text: 'Ação', value: 'action', sortable: false },
            { text: 'Por', value: 'performed_by', sortable: false },
            { text: 'Usuário', value: 'user_name', sortable: false },
        ],
    }),
    computed: {
         ...mapState('auth', ['user']),
        changesRows() {
            if (!this.changes || typeof this.changes !== 'object') {
                return []
            }

            const hasOldNew =
                Object.prototype.hasOwnProperty.call(this.changes, 'old') ||
                Object.prototype.hasOwnProperty.call(this.changes, 'new')

            if (!hasOldNew) {
                return Object.keys(this.changes)
                    .sort()
                    .map((key) => ({
                        key,
                        old: this.changes[key],
                        new: null,
                        changed: false,
                    }))
            }

            const oldObj = (this.changes && this.changes.old && typeof this.changes.old === 'object')
                ? this.changes.old
                : {}
            const newObj = (this.changes && this.changes.new && typeof this.changes.new === 'object')
                ? this.changes.new
                : {}

            const keys = Array.from(new Set([
                ...Object.keys(oldObj),
                ...Object.keys(newObj),
            ])).sort()

            return keys.map((key) => {
                const oldVal = Object.prototype.hasOwnProperty.call(oldObj, key) ? oldObj[key] : null
                const newVal = Object.prototype.hasOwnProperty.call(newObj, key) ? newObj[key] : null

                return {
                    key,
                    old: oldVal,
                    new: newVal,
                    changed: this.isDifferent(oldVal, newVal),
                }
            })
        },
    },
    watch: {
        dialog(val) {
            if (val) {
                this.fetchLogs()
            } else {
                this.logs = []
                this.selected = null
                this.changes = null
                this.errorMessage = ''
            }
        },
    },
    methods: {
        open() {
            this.dialog = true
        },
        stableStringify(value) {
            if (value === null || typeof value === 'undefined') {
                return 'null'
            }

            if (typeof value !== 'object') {
                return JSON.stringify(value)
            }

            if (Array.isArray(value)) {
                return '[' + value.map((v) => this.stableStringify(v)).join(',') + ']'
            }

            const keys = Object.keys(value).sort()
            const parts = keys.map((k) => JSON.stringify(k) + ':' + this.stableStringify(value[k]))
            return '{' + parts.join(',') + '}'
        },
        coerceComparable(value) {
            if (value === null || typeof value === 'undefined') {
                return null
            }

            if (typeof value === 'number' || typeof value === 'boolean') {
                return value
            }

            if (typeof value === 'string') {
                const s = value.trim()
                if (s === '') {
                    return ''
                }
                if (s === 'true') {
                    return true
                }
                if (s === 'false') {
                    return false
                }
                if (/^-?\d+(\.\d+)?$/.test(s)) {
                    const n = Number(s)
                    return Number.isNaN(n) ? s : n
                }
                return s
            }

            return this.stableStringify(value)
        },
        isDifferent(oldVal, newVal) {
            const oldIsObj = oldVal && typeof oldVal === 'object'
            const newIsObj = newVal && typeof newVal === 'object'

            if (oldIsObj || newIsObj) {
                return this.stableStringify(oldVal) !== this.stableStringify(newVal)
            }

            return this.coerceComparable(oldVal) !== this.coerceComparable(newVal)
        },
        formatValue(value) {
            if (value === null || typeof value === 'undefined') {
                return ''
            }

            if (typeof value === 'string') {
                return value
            }

            if (typeof value === 'number' || typeof value === 'boolean') {
                return String(value)
            }

            try {
                return JSON.stringify(value, null, 2)
            } catch (e) {
                return String(value)
            }
        },
        async fetchLogs() {
            this.loading = true
            this.errorMessage = ''
            this.logs = []
            this.selected = null
            this.changes = null

            try {
                const res = await service({
                    module: 'auditLog',
                    action: 'index',
                    payload: {
                        table_name: this.tableName,
                        record_id: this.recordId,
                        context_id: this.contextId,
                        per_page: 50,
                    },
                })

                this.logs = res.data || []
            } catch (e) {
                this.errorMessage = (e && e.message) ? e.message : 'Falha ao buscar logs.'
            } finally {
                this.loading = false
            }
        },
        async selectLog(item) {
            if (!item || !item.id) {
                return
            }

            this.selected = item
            this.changes = null
            this.payloadLoading = true
            this.errorMessage = ''

            try {
                const res = await service({
                    module: 'auditLog',
                    action: 'payload',
                    payload: { id: item.id },
                })

                const payload = res.data ? res.data.payload : null
                this.changes = payload && payload.changes ? payload.changes : null
            } catch (e) {
                this.errorMessage = (e && e.message) ? e.message : 'Falha ao carregar JSON.'
            } finally {
                this.payloadLoading = false
            }
        },
    },
}
</script>

<style scoped>
.audit-table-scroll {
    max-height: 360px;
    overflow-y: auto;
}

.audit-pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 12px;
    line-height: 1.35;
}

.audit-row-changed {
    background: #fff8e1;
}
</style>
